import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, Loader2, Clock, Sparkles, Utensils, Globe } from 'lucide-react';

// Translations
const translations = {
  tr: {
    comingSoon: 'Çok Yakında',
    title: 'Harika Bir Şeyler Hazırlıyoruz',
    subtitle: 'Size en iyi dijital menü ve restoran yönetim platformunu sunmak için yoğun bir şekilde çalışıyoruz. Lansman haberlerinden ilk siz haberdar olun!',
    feature1Title: 'QR Menü',
    feature1Desc: 'Şık dijital menüler',
    feature2Title: 'Kolay Yönetim',
    feature2Desc: 'Menüyü saniyeler içinde güncelleyin',
    feature3Title: 'Çoklu Dil',
    feature3Desc: 'Daha fazla müşteriye ulaşın',
    emailPlaceholder: 'E-posta adresinizi girin',
    notifyMe: 'Canlıya Geçince Haber Ver',
    subscribing: 'Kaydediliyor...',
    noSpam: 'Spam yok, söz veriyoruz. İstediğiniz zaman abonelikten çıkabilirsiniz.',
    successTitle: 'Listedesiniz!',
    successMessage: 'Lansman olduğunda size haber vereceğiz.',
    invalidEmail: 'Lütfen geçerli bir e-posta adresi girin',
    copyright: 'Tüm hakları saklıdır.',
    tagline: 'Dijital Menü & Restoran Yönetimi'
  },
  en: {
    comingSoon: 'Coming Soon',
    title: 'Something Amazing is Brewing',
    subtitle: "We're working hard to bring you the best digital menu and restaurant management platform. Be the first to know when we launch!",
    feature1Title: 'QR Menu',
    feature1Desc: 'Beautiful digital menus',
    feature2Title: 'Easy Management',
    feature2Desc: 'Update menu in seconds',
    feature3Title: 'Multi-language',
    feature3Desc: 'Reach more customers',
    emailPlaceholder: 'Enter your email address',
    notifyMe: 'Notify Me When Live',
    subscribing: 'Subscribing...',
    noSpam: 'No spam, we promise. Unsubscribe anytime.',
    successTitle: "You're on the list!",
    successMessage: "We'll notify you as soon as we launch.",
    invalidEmail: 'Please enter a valid email address',
    copyright: 'All rights reserved.',
    tagline: 'Digital Menu & Restaurant Management'
  }
};

const Maintenance = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [lang, setLang] = useState('tr');

  // Detect browser language on mount
  useEffect(() => {
    const browserLang = navigator.language || navigator.userLanguage;
    // Check if browser language starts with 'en'
    if (browserLang && browserLang.toLowerCase().startsWith('en')) {
      setLang('en');
    } else {
      setLang('tr'); // Default to Turkish
    }
  }, []);

  const t = translations[lang];

  const toggleLanguage = () => {
    setLang(lang === 'tr' ? 'en' : 'tr');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setErrorMessage(t.invalidEmail);
      return;
    }

    setStatus('loading');

    try {
      // Store in backend waitlist
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setStatus('success');
        setEmail('');
      } else {
        const data = await response.json();
        setStatus('error');
        setErrorMessage(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      // If backend not available, store in localStorage as fallback
      const waitlist = JSON.parse(localStorage.getItem('waitlist') || '[]');
      if (!waitlist.includes(email)) {
        waitlist.push(email);
        localStorage.setItem('waitlist', JSON.stringify(waitlist));
      }
      setStatus('success');
      setEmail('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-purple-700 flex items-center justify-center p-4">
      {/* Language Toggle */}
      <button
        onClick={toggleLanguage}
        className="absolute top-4 right-4 z-20 flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white hover:bg-white/20 transition-colors"
      >
        <Globe className="w-4 h-4" />
        <span className="text-sm font-medium">{lang.toUpperCase()}</span>
      </button>

      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-purple-400/20 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-2xl w-full text-center">
        {/* Logo */}
        <div className="mb-8">
          <div className="relative inline-block mb-6">
            {/* Glow effect behind logo */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-400 via-pink-400 to-indigo-400 rounded-3xl blur-xl opacity-60 scale-110" />
            <div className="absolute inset-0 bg-white rounded-3xl blur-md opacity-40" />
            {/* Logo container */}
            <div className="relative bg-gradient-to-br from-white via-white to-purple-50 rounded-3xl px-8 py-6 shadow-2xl border border-white/50">
              <img src="/idemenu-logo.svg" alt="idemenu" className="h-14 w-auto" />
            </div>
          </div>
          <p className="text-purple-200 text-lg">{t.tagline}</p>
        </div>

        {/* Coming Soon Card */}
        <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 md:p-12 border border-white/20 shadow-2xl">
          <div className="inline-flex items-center gap-2 bg-purple-500/30 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Clock className="w-4 h-4" />
            {t.comingSoon}
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t.title}
          </h2>
          
          <p className="text-purple-100 text-lg mb-8 max-w-md mx-auto">
            {t.subtitle}
          </p>

          {/* Features Preview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            <div className="bg-white/10 rounded-xl p-4">
              <Sparkles className="w-6 h-6 text-yellow-300 mx-auto mb-2" />
              <p className="text-white text-sm font-medium">{t.feature1Title}</p>
              <p className="text-purple-200 text-xs">{t.feature1Desc}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <Utensils className="w-6 h-6 text-green-300 mx-auto mb-2" />
              <p className="text-white text-sm font-medium">{t.feature2Title}</p>
              <p className="text-purple-200 text-xs">{t.feature2Desc}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <CheckCircle className="w-6 h-6 text-blue-300 mx-auto mb-2" />
              <p className="text-white text-sm font-medium">{t.feature3Title}</p>
              <p className="text-purple-200 text-xs">{t.feature3Desc}</p>
            </div>
          </div>

          {/* Email Subscription Form */}
          {status === 'success' ? (
            <div className="bg-green-500/20 border border-green-400/30 rounded-xl p-6">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <h3 className="text-xl font-semibold text-white mb-2">{t.successTitle}</h3>
              <p className="text-green-200">{t.successMessage}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-300" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setStatus('idle');
                  }}
                  placeholder={t.emailPlaceholder}
                  className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all"
                />
              </div>
              
              {status === 'error' && (
                <p className="text-red-300 text-sm">{errorMessage}</p>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full py-4 bg-white text-purple-700 font-semibold rounded-xl hover:bg-purple-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t.subscribing}
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5" />
                    {t.notifyMe}
                  </>
                )}
              </button>

              <p className="text-purple-300 text-xs">
                {t.noSpam}
              </p>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-purple-300 text-sm">
          <p>© {new Date().getFullYear()} idemenu. {t.copyright}</p>
        </div>
      </div>
    </div>
  );
};

export default Maintenance;
